import { useState, useEffect, useCallback } from 'react';
import claimService from '../service/claimService.mjs';
import monthlyReportService from '../service/monthlyReportService.mjs';
import ptaeService from '../service/ptaeService.mjs';
import { secureLogger } from '../../utils/secureLogger.mjs';



export const useMonthlyReport = (reportId = null) => {
  const [report, setReport] = useState(null);
  const [ptaes, setPtaes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedClaims, setSelectedClaims] = useState(new Set());
  const [approvalChain, setApprovalChain] = useState(null);
  const [integrityStatus, setIntegrityStatus] = useState(null);

 

  // Load PTAE data
  const loadPTAEs = useCallback(async () => {
    try {
      const ptaeData = await ptaeService.getPTAEs();
      setPtaes(ptaeData);
    } catch (err) {
      secureLogger.error('Failed to load PTAEs', { status: err.response?.status });
    }
  }, []);

  // Load report data
  const loadReport = useCallback(async (id) => {
    if (!id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const reportData = await monthlyReportService.getMonthlyReport(id);
      setReport(reportData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch claims eligible for a month/region (for the review + defer step)
  const fetchEligibleClaims = useCallback(async (month, year, region) => {
    setLoading(true);
    setError(null);
    try {
      return await monthlyReportService.getEligibleClaims(month, year, region);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Generate new monthly report. excludeClaimIds = deferred claims.
  const generateReport = useCallback(async (month, year, region, excludeClaimIds = []) => {
    setLoading(true);
    setError(null);

    try {
      const newReport = await monthlyReportService.generateMonthlyReport(month, year, region, excludeClaimIds);
      setReport(newReport);
      return newReport;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update claim in report
const updateClaim = useCallback((claimId, fieldOrUpdater, value) => {
  setReport(prevReport => {
    if (!prevReport) return prevReport;

    const updatedClaims = prevReport.claims.map(claim => {
      // Use toString comparison to handle type mismatches (number vs string)
      if (String(claim.claimId) !== String(claimId)) return claim;

      // Case 1: function updater
      if (typeof fieldOrUpdater === "function") {
        return fieldOrUpdater(claim);
      }

      // Case 2: object with multiple fields
      if (typeof fieldOrUpdater === "object" && fieldOrUpdater !== null) {
        return { ...claim, ...fieldOrUpdater };
      }

      // Case 3: single field + value
      if (typeof fieldOrUpdater === "string") {
        return { ...claim, [fieldOrUpdater]: value };
      }

      return claim;
    });

    const totalAmount = updatedClaims.reduce(
      // allocatedAmount arrives as a DECIMAL string; parseFloat prevents string
      // concatenation ("0" + "1.2" = "01.2") that renders the total as NaN.
      (sum, claim) => sum + (parseFloat(claim.allocatedAmount) || 0),
      0
    );

    return {
      ...prevReport,
      claims: updatedClaims,
      totalAmount,
    };
  });
}, []);

  // Valid statuses for MonthlyReport model
  const VALID_STATUSES = ['draft', 'submitted', 'auditor_approved', 'regional_director_approved', 'global_director_approved', 'rejected'];

  // Save report changes
  const saveReport = useCallback(async (status = null, comments = '', commentType = 'auditor') => {
  if (!report) return;

  setSaving(true);
  setError(null);

  try {
    const updateData = {
      claims: report.claims
    };

    // Only include status if it's a valid status
    if (status && VALID_STATUSES.includes(status)) {
      updateData.status = status;
    }

    // Add comments to the correct field based on comment type
    if (commentType === 'admin') {
      updateData.adminComments = comments;
    } else if (commentType === 'auditor') {
      updateData.auditorComments = comments;
    } else if (commentType === 'regional_director') {
      updateData.regionalDirectorComments = comments;
    } else if (commentType === 'global_director' || commentType === 'finance_director') {
      updateData.globalDirectorComments = comments;
    }

    // Preserve existing comments from other roles
    if (report.adminComments && commentType !== 'admin') {
      updateData.adminComments = report.adminComments;
    }
    if (report.auditorComments && commentType !== 'auditor') {
      updateData.auditorComments = report.auditorComments;
    }
    if (report.regionalDirectorComments && commentType !== 'regional_director') {
      updateData.regionalDirectorComments = report.regionalDirectorComments;
    }
    if (report.globalDirectorComments && commentType !== 'global_director' && commentType !== 'finance_director') {
      updateData.globalDirectorComments = report.globalDirectorComments;
    }

    const updatedReport = await monthlyReportService.updateMonthlyReport(report._id, updateData);
    setReport(updatedReport);
    return updatedReport;
  } catch (err) {
    setError(err.message);
    throw err;
  } finally {
    setSaving(false);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [report]);


  // Persist an explicit claims array to the backend immediately (no status
  // change, no PTAE gate). Used by approve/reject actions so a claim's status
  // survives a page refresh instead of living only in local state.
  const persistReportClaims = useCallback(async (nextClaims, extra = {}) => {
    if (!report) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await monthlyReportService.updateMonthlyReport(report._id, { claims: nextClaims, ...extra });
      setReport(updated);
      return updated;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  // Get claim details
  const getClaimDetails = useCallback(async (claimId, claimType) => {
    try {
      return await claimService.getClaimDetails(claimId, claimType);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  // Toggle claim selection
  const toggleClaimSelection = useCallback((claimId) => {
    setSelectedClaims(prev => {
      const newSet = new Set(prev);
      if (newSet.has(claimId)) {
        newSet.delete(claimId);
      } else {
        newSet.add(claimId);
      }
      return newSet;
    });
  }, []);

  // Bulk approve/reject selected claims
  const bulkUpdateClaims = useCallback((status, comments = '') => {
    setReport(prevReport => {
      if (!prevReport) return prevReport;
      
      const updatedClaims = prevReport.claims.map(claim =>
        selectedClaims.has(claim.claimId)
          ? { ...claim, status, comments }
          : claim
      );
      
      return {
        ...prevReport,
        claims: updatedClaims
      };
    });
    
    setSelectedClaims(new Set());
  }, [selectedClaims]);

  const deleteReport = async () => {
    try {
      setSaving(true);
      await monthlyReportService.deleteReport(reportId);
      return { success: true };
    } catch (error) {
      secureLogger.error('Error deleting report', { status: error.response?.status });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Submit report for approval (admin -> auditor)
  const submitReport = useCallback(async (comments = '') => {
    if (!report) return;

    setSaving(true);
    setError(null);

    try {
      const result = await monthlyReportService.submitReport(report._id, comments);
      setReport(prev => ({ ...prev, ...result.data }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [report]);

  // Approve report (auditor, regional_director, global_director, finance_director).
  // `options.acknowledgeOpenQuestions` carries on past the open-questions
  // warning; that warning (code OPEN_QUESTIONS) is a question for the
  // approver, not an error, so it is never put up as one.
  const approveReport = useCallback(async (comments = '', options = {}) => {
    if (!report) return;

    setSaving(true);
    setError(null);

    try {
      const result = await monthlyReportService.approveReport(report._id, comments, options);
      setReport(prev => ({ ...prev, ...result.data }));
      return result;
    } catch (err) {
      if (err?.code !== 'OPEN_QUESTIONS') setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [report]);

  // Reject report (requires reason)
  const rejectReport = useCallback(async (reason, comments = '') => {
    if (!report) return;

    if (!reason?.trim()) {
      throw new Error('Rejection reason is required');
    }

    setSaving(true);
    setError(null);

    try {
      const result = await monthlyReportService.rejectReport(report._id, reason, comments);
      setReport(prev => ({ ...prev, ...result.data }));
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [report]);

  // Load approval chain (audit trail)
  const loadApprovalChain = useCallback(async (id = reportId) => {
    if (!id) return;

    try {
      const result = await monthlyReportService.getApprovalChain(id);
      setApprovalChain(result.data);
      return result.data;
    } catch (err) {
      secureLogger.error('Failed to load approval chain', { status: err.response?.status });
      setError(err.message);
      throw err;
    }
  }, [reportId]);

  // Individually audit a single claim within the current report
  const auditClaimInReport = useCallback(async (claimId, claimType, comments = '', options = {}) => {
    try {
      await monthlyReportService.auditClaim(claimId, claimType, comments, options);
      const note = (comments || '').trim();
      setReport(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          claims: prev.claims.map(c =>
            String(c.claimId) === String(claimId)
              ? {
                  ...c,
                  isAuditorApproved: true,
                  auditorApprovedAt: new Date().toISOString(),
                  status: 'approved',
                  // The server writes the audit note onto the report row too, so
                  // mirror it here rather than leaving the row's Comments cell
                  // looking empty until the next reload.
                  ...(note ? { auditorComments: note, comments: c.comments || note } : {}),
                }
              : c
          ),
        };
      });
    } catch (err) {
      if (err?.code !== 'OPEN_QUESTIONS') setError(err.message);
      throw err;
    }
  }, []);

  // Mark a single claim as reviewed during the guided walkthrough. The backend
  // decides what that means for the caller's role (the auditor's mark is the real
  // individual audit; a director's is per-claim review progress), so patch the
  // matching field here. Keyed on (claimId, claimType) - never on the report-claim
  // row id, which is regenerated every time the report is saved.
  const markClaimReviewed = useCallback(async (claimId, claimType, role, comments = '', options = {}) => {
    try {
      await monthlyReportService.markClaimReviewed(reportId, claimId, claimType, comments, options);
      const now = new Date().toISOString();
      const patch =
        role === 'auditor'
          ? { isAuditorApproved: true, auditorApprovedAt: now, status: 'approved' }
          : role === 'regional_director'
            ? { regionalDirectorReviewedAt: now }
            : { globalDirectorReviewedAt: now };

      setReport(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          claims: prev.claims.map(c =>
            String(c.claimId) === String(claimId) && c.claimType === claimType
              ? { ...c, ...patch }
              : c
          ),
        };
      });
    } catch (err) {
      if (err?.code !== 'OPEN_QUESTIONS') setError(err.message);
      throw err;
    }
  }, [reportId]);

  // Flag / unflag a single claim for follow-up. Flags are role-scoped server-side,
  // so the response carries the merged set and we take it verbatim rather than
  // guessing how the caller's flag combined with the others.
  const flagClaimInReport = useCallback(async (claimId, claimType, reason) => {
    try {
      const result = await monthlyReportService.flagClaim(reportId, claimId, claimType, reason);
      const { reviewFlags, flagged, flagReason } = result?.data || {};

      setReport(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          claims: prev.claims.map(c =>
            String(c.claimId) === String(claimId) && c.claimType === claimType
              ? { ...c, reviewFlags, flagged, flagReason }
              : c
          ),
        };
      });
      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, [reportId]);

  // Verify report integrity
  const verifyIntegrity = useCallback(async (id = reportId) => {
    if (!id) return;

    try {
      const result = await monthlyReportService.verifyIntegrity(id);
      setIntegrityStatus(result.data);
      return result.data;
    } catch (err) {
      secureLogger.error('Failed to verify integrity', { status: err.response?.status });
      setError(err.message);
      throw err;
    }
  }, [reportId]);

  

  // Load data on mount
  useEffect(() => {
    loadPTAEs();
    if (reportId) {
      loadReport(reportId);
    }
  }, [reportId, loadPTAEs, loadReport]);

  return {
    report,
    ptaes,
    loading,
    error,
    saving,
    selectedClaims, setSelectedClaims,
    approvalChain,
    integrityStatus,
    generateReport,
    fetchEligibleClaims,
    loadReport,
    updateClaim,
    saveReport,
    persistReportClaims,
    getClaimDetails,
    toggleClaimSelection,
    bulkUpdateClaims,
    setError,
    setReport,
    deleteReport,
    submitReport,
    approveReport,
    rejectReport,
    auditClaimInReport,
    markClaimReviewed,
    flagClaimInReport,
    loadApprovalChain,
    verifyIntegrity
  };
};



